import { parseDriverAddress, type DrivenVersionFields } from '@agavi/blocks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope } from './answer-ref-context';
import { useBlockIdentity } from './fired-states';


/**
 * WHICH TASKS DEPEND ON THIS OPTION — the tasks, not the blocks in them.
 *
 * Deleting an option on a driver is not like deleting the block. `syncDrivenVersions` KEEPS
 * the version whose row is gone, on purpose — the authored question survives in case the
 * option comes back — so nothing is lost and the delete must not be refused. What was missing
 * is that nothing SAID so, in either direction: no warning before, and (until
 * `driven-version-stranded`) no report after.
 *
 * So this is the before. The scenario side has had its equivalent since §7.3:
 * `followersLosingAnswers` names the followers that would lose an authored answer when a
 * variant goes, and stays quiet for untouched copies. Same shape, one axis over.
 *
 * ## It is a WARNING, never a refusal
 *
 * The block-level guard bans a load-bearing delete because losing the driver leaves a block
 * with no way to choose among its questions. Losing one OPTION leaves the axis working and one
 * version unreachable — recoverable by putting the option back, and visible in the issue list
 * meanwhile. Refusing it would make a driver's options undeletable, which is the cure being
 * worse.
 *
 * ## Called by the row, not threaded to it
 *
 * Each option row asks for itself and reads the block's identity from the context it already
 * sits in — the arrangement `useDrivenLinkIssues` and `VariesWithSection` use. Threading a map
 * of warnings through five different `SortableOptionRow` prop lists is five chances for one of
 * them to be forgotten, which is the shape this feature has already been bitten by twice.
 *
 * The scan is memoised on the scope, so N rows cost N walks of the task list per change rather
 * than per render, over a list that is tens of blocks.
 */
export function useDrivenRowDependents(rowId: string | undefined): string[] {
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();
  const selfId = identity?.blockId;
  const tasks = scope?.tasks;

  return useMemo(() => {
    if (!rowId || !selfId || !tasks) return [];
    const names: string[] = [];
    for (const task of tasks) {
      for (const block of task.blocks ?? []) {
        const data = block.data as
          { drivenBy?: string; versions?: ({ id: string } & DrivenVersionFields)[] } | undefined;
        /*
         * Pointing at THIS block, whatever facet it names. A FITB word id is unique across
         * the passage, so matching the row is enough and the facet adds nothing — checking it
         * as well would be a second rule to keep in step with the first.
         */
        /* An ALIAS link points at several blocks, so "does it point at me" is membership.
           Its rows are the union's, not this block's, so a row id matches only where this
           block actually contributed it — which the version check below already asks. */
        const address = parseDriverAddress(data?.drivenBy);
        if (!address) continue;
        if (address.kind === 'alias') {
          const alias = (scope?.aliases ?? []).find(a => a.id === address.aliasId);
          if (!alias?.members.some(m => m.blockId === selfId)) continue;
        } else if (address.blockId !== selfId) continue;
        if (!data?.versions?.some(v => v.drivenRowId === rowId)) continue;
        /*
         * THE TASK, not the block. Owner: "when you are outside of the task, just knowing
         * that it's connected to another task is sufficient, the details of which block
         * isn't important" — said of the canvas pill and true here for the same reason. A
         * driven block is always in another task, so a bare block name names something the
         * teacher cannot see from where they are standing, and the task is where they would
         * go to look.
         */
        names.push(task.title || task.id);
      }
    }
    /* Deduped: three blocks in one task is one place to go, and naming it three times is
       the list a teacher stops reading. */
    return [...new Set(names)];
  }, [rowId, selfId, tasks, scope?.aliases]);
}

/**
 * The same question, answered as the sentence an armed delete says.
 *
 * In the LABEL and the tooltip rather than a line that mounts on arm. That hazard is recorded
 * in `feedback_arm_hint_layout_shift`: mounting anything near a two-click delete inside a
 * scroller changes the container's height, the browser scrolls the button out from under a
 * stationary cursor, and the resulting `pointerleave` disarms it before the second click can
 * land. `version-selector` solves that with a settle window because its warning has to be
 * visible; here the armed button already changes what it says, so the warning can ride the
 * text that was changing anyway and cost no layout at all.
 */
export function useDrivenRowDeleteWarning(rowId: string | undefined): string | undefined {
  const names = useDrivenRowDependents(rowId);
  const { t } = useTranslation();
  if (names.length === 0) return undefined;
  return t('blocks.drivenVersions.optionStrands', {
    count: names.length, names: names.join(', '),
  });
}
